/**
 * WidgetHeader - заголовок виджета
 *
 * Содержит:
 * - Переключатель режимов voice/chat (опционально)
 * - Заголовок и индикатор статуса сессии
 * - Кнопки управления звуком (speaker/mic)
 * - Кнопки управления окном (minimize/maximize/close)
 */

import { memo } from 'react';
import PropTypes from 'prop-types';
import { Minus, ArrowsOut as Maximize2, ArrowsIn as Minimize2, X, SpeakerHigh as Volume2, SpeakerSlash as VolumeX, Microphone as Mic, MicrophoneSlash as MicOff, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { VoiceIcon, ChatIcon } from './icons';
import { t } from './translations';
import { languagePropType } from './utils/propTypes';
import './WidgetHeader.css';

export const WidgetHeader = memo(({
  // Язык подписей для скринридера (#1431); у дилеров не передаётся — en.
  language = 'en',
  title = 'Assistant',
  isSessionActive = false,
  // Mode toggle
  showModeToggle = false,
  currentMode = 'chat', // 'voice' | 'chat'
  onModeChange,
  isSpeaking = false,
  isUserSpeaking = false,
  // Speaker controls
  speakerMuted = false,
  onSpeakerToggle,
  showSpeakerButton = true,
  // Microphone controls
  micMuted = false,
  onMicToggle,
  showMicButton = true,
  // Window controls
  widgetState = 'normal', // 'minimized' | 'normal' | 'maximized'
  onMinimize,
  onMaximize,
  onRestore,
  onClose,
  onCollapseToVoice, // NEW: Collapse chat to voice/collapsed mode
  // Drag handle
  isDraggable = false,
  onDragStart,
  // Mode switching indicator
  isModeChanging = false,
}) => {
  return (
    <div
      className={`widget-header ${isDraggable ? 'widget-header--draggable' : ''}`}
      onMouseDown={isDraggable ? onDragStart : undefined}
    >
      {/* Left: Mode toggle OR Title + status */}
      <div className="widget-header__left">
        {showModeToggle ? (
          <div className="widget-header__mode-toggle">
            <button
              className={`widget-header__mode-btn ${currentMode === 'voice' ? 'widget-header__mode-btn--active' : ''} ${isModeChanging && currentMode !== 'voice' ? 'widget-header__mode-btn--loading' : ''}`}
              onClick={() => !isModeChanging && onModeChange?.('voice')}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={t('switchToVoice', language)}
              title={isModeChanging ? 'Connecting...' : 'Voice'}
              disabled={isModeChanging}
            >
              {isModeChanging && currentMode !== 'voice' ? (
                <Loader2 size={20} className="widget-header__spinner" />
              ) : (
                <VoiceIcon size={20} />
              )}
            </button>
            <button
              className={`widget-header__mode-btn ${currentMode === 'chat' ? 'widget-header__mode-btn--active' : ''}`}
              onClick={() => !isModeChanging && onModeChange?.('chat')}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={t('switchToChat', language)}
              title="Chat"
              disabled={isModeChanging}
            >
              <ChatIcon size={20} />
            </button>
          </div>
        ) : (
          <>
            <h3 className="widget-header__title">{title}</h3>
            <div
              className={`widget-header__status ${isSessionActive ? 'widget-header__status--active' : ''}`}
              title={isSessionActive ? 'Session active' : 'Session inactive'}
            />
          </>
        )}
      </div>

      {/* Right: Controls */}
      <div className="widget-header__controls">
        {/* Speaker toggle */}
        {showSpeakerButton && onSpeakerToggle && (
          <button
            className="widget-header__btn"
            onClick={onSpeakerToggle}
            aria-label={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
            title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
          >
            {speakerMuted ? (
              <VolumeX size={18} weight="light" />
            ) : (
              <Volume2 size={18} weight="light" />
            )}
          </button>
        )}

        {/* Microphone toggle */}
        {showMicButton && onMicToggle && (
          <button
            className="widget-header__btn"
            onClick={onMicToggle}
            aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
            title={micMuted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {micMuted ? (
              <MicOff size={18} weight="light" />
            ) : (
              <Mic size={18} weight="light" />
            )}
          </button>
        )}

        {/* Minimize */}
        {onMinimize && (
          <button
            className="widget-header__btn"
            onClick={onMinimize}
            aria-label="Minimize"
            title="Minimize"
          >
            <Minus size={18} weight="light" />
          </button>
        )}

        {/* Maximize / Restore */}
        {widgetState === 'maximized' ? (
          onRestore && (
            <button
              className="widget-header__btn"
              onClick={onRestore}
              aria-label="Restore"
              title="Restore"
            >
              <Minimize2 size={18} weight="light" />
            </button>
          )
        ) : (
          onMaximize && (
            <button
              className="widget-header__btn"
              onClick={onMaximize}
              aria-label="Maximize"
              title="Maximize"
            >
              <Maximize2 size={18} weight="light" />
            </button>
          )
        )}

        {/* Collapse to voice/collapsed */}
        {onCollapseToVoice && (
          <button
            className="widget-header__btn"
            onClick={onCollapseToVoice}
            aria-label="Collapse"
            title="Collapse"
          >
            <Minus size={18} weight="light" />
          </button>
        )}

        {/* Close. По наличию обработчика — как «свернуть» рядом: встроенный в
            страницу чат закрывать некуда, и кнопка там вела бы в никуда.
            Поведение вызывающих не меняется: обработчик передают все, кроме
            инлайн-режима. */}
        {onClose && (
          <button
            className="widget-header__btn widget-header__btn--close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <X size={18} weight="light" />
          </button>
        )}
      </div>
    </div>
  );
});

WidgetHeader.displayName = 'WidgetHeader';

WidgetHeader.propTypes = {
  language: languagePropType,
  title: PropTypes.string,
  isSessionActive: PropTypes.bool,
  // Mode toggle
  showModeToggle: PropTypes.bool,
  currentMode: PropTypes.oneOf(['voice', 'chat']),
  onModeChange: PropTypes.func,
  isSpeaking: PropTypes.bool,
  isUserSpeaking: PropTypes.bool,
  // Speaker controls
  speakerMuted: PropTypes.bool,
  onSpeakerToggle: PropTypes.func,
  showSpeakerButton: PropTypes.bool,
  // Microphone controls
  micMuted: PropTypes.bool,
  onMicToggle: PropTypes.func,
  showMicButton: PropTypes.bool,
  // Window controls
  widgetState: PropTypes.oneOf(['minimized', 'normal', 'maximized']),
  onMinimize: PropTypes.func,
  onMaximize: PropTypes.func,
  onRestore: PropTypes.func,
  onClose: PropTypes.func,
  onCollapseToVoice: PropTypes.func,
  // Drag handle
  isDraggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  // Mode switching indicator
  isModeChanging: PropTypes.bool,
};
